<template>
  <div class="dropdown-menu-wrapper">
    <Button
      ref="buttonRef"
      :label="buttonText"
      icon="pi pi-angle-down"
      iconPos="right"
      severity="secondary"
      size="small"
      data-testid="dropdown-menu-trigger"
      @click="toggle"
      aria-haspopup="true"
      aria-controls="overlay_menu"
    />
    <Menu
      ref="menuRef"
      id="overlay_menu"
      :model="menuItems"
      :popup="true"
      @select="handleSelect"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import Button from 'primevue/button';
import Menu from 'primevue/menu';

const props = withDefaults(defineProps<{
  items?: any[];
  labelField?: string;
  valueField?: string;
  buttonText?: string;
  emptyText?: string;
  zIndex?: number;
  topOffsetPx?: number;
}>(), {
  items: () => [],
  labelField: 'label',
  valueField: 'value',
  buttonText: 'Abrir',
  emptyText: 'Sem itens',
  zIndex: 2050,
  topOffsetPx: 30
});

const emit = defineEmits<{
  select: [item: any];
  open: [];
  close: [];
}>();

const buttonRef = ref<InstanceType<typeof Button> | null>(null);
const menuRef = ref<InstanceType<typeof Menu> | null>(null);

// Convert items to PrimeVue Menu format
const menuItems = computed(() => {
  if (!props.items || props.items.length === 0) {
    return [
      {
        label: props.emptyText,
        disabled: true
      }
    ];
  }

  return props.items.map((item: any) => ({
    label: getLabel(item),
    command: () => {
      emit('select', item);
      emit('close');
    }
  }));
});

const toggle = (event: Event) => {
  if (menuRef.value) {
    // PrimeVue Menu's toggle method will use event.currentTarget for positioning
    // Since the button is the direct click target, it should work correctly
    (menuRef.value as any).toggle(event);
    emit('open');
  }
};

const getLabel = (item: any): string => item?.[props.labelField] ?? '';
const getKey = (item: any): string => item?.[props.valueField] ?? getLabel(item);

const handleSelect = () => {
  emit('close');
};

onMounted(() => {
  // Menu component handles outside clicks internally
});

onBeforeUnmount(() => {
  // Cleanup handled by Menu component
});
</script>

<style scoped>
.dropdown-menu-wrapper {
  position: relative;
  display: inline-block;
}

.dropdown-trigger {
  cursor: pointer;
}

:deep(.p-menu) {
  max-height: 320px;
  overflow: auto;
}
</style>

<style>
/* Global style to ensure PrimeVue Menu overlays appear above modals */
.p-menu.p-menu-overlay {
  z-index: 9999 !important;
}
</style>
